<template>
  <div>
    <div v-if="$userInfo.isSupportUser || $userInfo.isCustomerAdministrator || $userInfo.isSystemAdministrator">
      <div class="tile is-parent pl-1">
        <article class="tile is-child box">
          <div class="columns is-flex is-align-items-center">
            <div class="column">
              <p class="title">Detail Info</p>
            </div>
            <div v-if="!hasError && showSavingStatus && !toastNotificationEnabled"
              class="column is-flex is-justify-content-flex-end is-align-items-center">
              <span v-if="saving"
                class="button is-loading is-white"
                style="height: 27px;" />
              <span v-else
                class="text has-text-success">
                <span class="mso mso-18px">check_circle</span>
              </span>
              <span class="is-size-6"
                :class="saving ? 'has-text-warning' : 'has-text-success'">{{ savingStatus }}</span>
            </div>
          </div>
          <div class="field columns">
            <div class="column is-half">
              <label class="label">Name</label>
              <div class="control">
                <input class="input"
                  type="text"
                  v-model="companyDetails.name"
                  :disabled="!$userInfo.isSystemAccount">
                <span class="help is-danger"
                  v-if="!$v.companyDetails.name.required">Name is required
                </span>
                <span class="help is-danger"
                  v-if="!$v.companyDetails.name.companynameUnique">Company name is already in use.
                </span>
              </div>
            </div>
            <div class="column is-half">
              <label class="label">ABN</label>
              <div class="control">
                <the-mask v-if="companyDetails.countryCode === 'AU'"
                  class="input"
                  :mask="['## ### ### ###']"
                  v-model="companyDetails.abn"
                  :disabled="!$userInfo.isSystemAccount" />
                <input v-else
                  class="input"
                  type="text"
                  v-model="companyDetails.abn"
                  :disabled="!$userInfo.isSystemAccount">
                <span class="help is-danger"
                  v-if="!$v.companyDetails.abn.required">ABN is required
                </span>
              </div>
            </div>
          </div>
          <div class="field columns">
            <div class="column is-half pb-0">
              <label class="label">Customer Since</label>
              <input v-if="!$userInfo.isSystemAccount"
                class="input"
                :value="$filters.formatDateLocale(innerCustomerSince, $userInfo.locale)"
                disabled>
              <v-date-picker v-else
                :is-dark="$theme.isDark"
                v-model="innerCustomerSince"
                :masks="formats"
                :attributes="attrs"
                :locale="$userInfo.locale"
                :max-date="today"
                @input="customerSinceInputEvent"
                class="is-fullwidth"
                :timezone="$filters.getIanaTimezone()">
                <template v-slot="{ inputValue, inputEvents }">
                  <div class="field has-addons">
                    <div class="control is-expanded has-icons-left">
                      <input type="text"
                        class="input"
                        placeholder="Customer Since"
                        :value="inputValue"
                        v-on="inputEvents">
                      <span class="icon is-small is-left">
                        <span class="mso mso-18px">calendar_month</span>
                      </span>
                    </div>
                  </div>
                </template>
              </v-date-picker>
            </div>
            <div class="column is-half pb-0">
              <label class="label">Registered ID</label>
              <div class="control">
                <input class="input"
                  type="number"
                  maxlength="6"
                  v-model="companyDetails.companyRegisteredId"
                  :disabled="!$userInfo.isSystemAccount"
                  @input="onRegisteredIdInput"
                  @keydown="preventNonDigit">
                <span class="help is-danger"
                  v-if="!$v.companyDetails.companyRegisteredId.required">Registered ID is required
                </span>
                <span class="help is-danger"
                  v-if="!$v.companyDetails.companyRegisteredId.companyRegisteredIdUnique">Registered ID is already in use.
                </span>
              </div>
            </div>
          </div>
          <div class="field columns"
            v-if="!$userInfo.isSupportUser && $userInfo.isSystemAdministrator && !companyDetails.isHq && !isHQ">
            <div class="column is-half pb-0">
              <label class="label">HQ Company</label>
              <div class="control">
                <bulma-autocomplete placeholder="HQ Company"
                  v-model="selectedHqCompanyNo"
                  :data="hqCompanyNoOptions"
                  field="companyLoginId"
                  :loading="hqCompanyNoLoading"
                  @typing="onHqCompanyNoTyping"
                  @select="onHqSelected($event)">
                  <template slot-scope="props">
                    {{ `${props.option.companyLoginId}` }}
                  </template>
                </bulma-autocomplete>
                <!-- <Multiselect v-model="selectedHqCompanyNo"
                  :options="hqCompanyNoOptions"
                  :hide-selected="true"
                  :multiple="true"
                  :clear-on-select="false"
                  :close-on-select="true"
                  :show-labels="false"
                  :searchable="true"
                  :internal-search="true"
                  :allow-empty="true"
                  :loading="hqCompanyNoLoading"
                  :label="'companyLoginId'"
                  :value="'companyLoginId'"
                  :track-by="'companyLoginId'"
                  placeholder="Select HQ Company"
                  @search-change="loadHQCompaniesData"
                  @input="onHqSelected($event)">
                  <span class="has-text-danger"
                    slot="noResult">No HQ Company found.</span>
                </Multiselect> -->
              </div>
            </div>
            <div class="column is-half pb-0">
              <label class="label">Parent Company Name</label>
              <div class="control">
                <input class="input"
                  type="text"
                  v-model="companyDetails.parentCompanyName"
                  :disabled="true">
              </div>
            </div>
          </div>
          <hr>

          <div class="field columns">
            <div class="column is-half pt-0 mt-3">
              <div class="mb-5">
                <label class="label">Country</label>
                <div class="control tooltip"
                  data-tooltip="Country of Default Address">
                  <div class="select is-fullwidth">
                    <select v-model="companyDetails.countryCode"
                      :disabled="!$userInfo.isSupportUser && !$userInfo.isSystemAdministrator">
                      <option value="null"
                        disabled>Select</option>
                      <option v-for="(country) in countries"
                        :key="country.countryCode"
                        :value="country.countryCode">
                        {{ country.countryDesc }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
              <div class="mb-5">
                <label class="label">Time Zone</label>
                <div class="control">
                  <div class="select is-fullwidth">
                    <select v-model="companyDetails.timezone"
                      :disabled="!companyDetails.countryCode || (!$userInfo.isSupportUser && !$userInfo.isSystemAdministrator)">
                      <option value="null"
                        disabled>Select</option>
                      <option v-for="(timezone) in timezones"
                        :key="timezone.Id"
                        :value="timezone.DisplayName">
                        {{ timezone.DisplayName }}
                      </option>
                      <!-- <option v-for="(timezone, index) in timezones"
                        :key="index"
                        :value="timezone">
                        {{ timezone }}
                      </option> -->
                    </select>
                  </div>
                </div>
              </div>

              <div class="mb-5">
                <label class="label">Locale</label>
                <div class="control">
                  <div class="select is-fullwidth">
                    <select v-model="companyDetails.locale"
                      :disabled="!companyDetails.countryCode || (!$userInfo.isSupportUser && !$userInfo.isSystemAdministrator)">
                      <option value="null"
                        disabled>Select</option>
                      <option v-for="(locale, index) in locales"
                        :key="index"
                        :value="locale">
                        {{ locale }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div class="is-divider-vertical p-0" />
            <div class="column is-half pb-0">
              <div class="field">
                <company-asset-addresses ref="assetAddresses"
                  :addresses="sortedAddresses"
                  :country-code="companyDetails.countryCode"
                  @removeAddress="removeAddress"
                  @setDefault="setDefault"
                  @addTmpAddress="addTmpAddress" />
              </div>
            </div>
          </div>
          <hr>

          <div class="field columns">
            <div class="column is-half">
              <company-asset-phones ref="assetPhones"
                :phones="sortedPhones"
                @removePhone="removePhone"
                @setDefault="setDefault"
                @addTmpPhone="addTmpPhone" />
            </div>

            <div class="column is-half">
              <company-asset-emails ref="assetEmails"
                :emails="sortedEmails"
                @removeEmail="removeEmail"
                @setDefault="setDefault"
                @addTmpEmail="addTmpEmail" />
            </div>
          </div>

          <div class="field columns mb-2">
            <div class="column is-half">
              <company-contact ref="assetContacts"
                v-if="$userInfo.isSystemAccount || $userInfo.isSystemAdministrator"
                :contacts="companyDetails.contacts"
                :company-id="companyDetails.companyId"
                @addContact="addContact"
                @removeContact="removeContact"
                @setDefault="setDefault" />
              <!-- <div class="is-flex is-justify-content-space-between">
                <label class="label">Contact Person</label>
                <button class="button is-small is-primary is-light"
                  :class="`is-light-btn-${$theme.name}`"
                  @click="addContact"
                  :disabled="!$userInfo.isSystemAccount">
                  <span class="mso mso-24px">add</span>
                </button>
              </div>
              <div v-if="companyDetails && companyDetails.contacts?.length > 0">
                <div class="field columns mb-0">
                  <div class="column is-half">
                    <label class="label has-text-weight-normal">First Name</label>
                  </div>
                  <div class="column is-half">
                    <label class="label has-text-weight-normal">Last Name</label>
                  </div>
                </div>
                <div v-for="(contact, index) in companyDetails.contacts"
                  :key="index"
                  class="field columns is-flex is-align-items-center">
                  <div class="column is-half">
                    <div class="control">
                      <input class="input"
                        type="text"
                        placeholder="First Name"
                        v-model="contact.firstName"
                        :disabled="!$userInfo.isSystemAccount">
                    </div>
                  </div>
                  <div class="field has-addons column is-half">
                    <div class="control is-expanded">
                      <input class="input"
                        type="text"
                        placeholder="Last Name"
                        v-model="contact.lastName"
                        :disabled="!$userInfo.isSystemAccount">
                    </div>
                    <div class="control">
                      <button class="button has-text-gold"
                        @click="setDefault(contact, 'contacts')"
                        :disabled="!$userInfo.isSystemAccount">
                        <span class="icon">
                          <span class="mso mso-24px"
                            :class="[contact.isDefault ? 'mso-fill' : '']">
                            star
                          </span>
                        </span>
                      </button>
                    </div>
                    <div class="control">
                      <button class="button has-text-danger"
                        @click="removeContact(contact)"
                        :disabled="!$userInfo.isSystemAccount">
                        <span class="icon">
                          <span class="mso mso-24px">remove</span>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else
                class="label has-text-weight-normal ml-1">No Contacts</div> -->
            </div>
          </div>

          <div class="field columns"
            v-if="$userInfo.isSystemAccount && !isNew">
            <div class="column is-half pb-0">
              <label class="label">Reset User Password</label>
              <button class="button is-primary tooltip"
                data-tooltip="Reset Password"
                @click="resetPassword">
                <span>Reset Password</span>
              </button>
            </div>
          </div>

          <div class="is-divider"
            v-if="$userInfo.isSystemAccount" />
          <div class="field"
            v-if="$userInfo.isSystemAccount">
            <div class="buttons has-addons is-centered">
              <span class="button"
                :class="{ 'is-success is-selected' : companyDetails.isActive }"
                @click="toggleActive(true)">
                <span class="icon is-small">
                  <i class="mdi mdi-check" />
                </span>
                <span>Active</span>
              </span>
              <span class="button"
                :class="{ 'is-danger is-selected' : !companyDetails.isActive }"
                @click="toggleActive(false)">
                <span class="icon is-small">
                  <i class="mdi mdi-close" />
                </span>
                <span>In-Active</span>
              </span>
            </div>
          </div>
        </article>
      </div>
      <div class="tile is-parent pl-1"
        v-if="$userInfo.isSystemAdministrator && isNew && user">
        <article class="tile is-child box">
          <div class="columns is-flex is-align-items-center">
            <div class="column">
              <p class="title">Default Administrator</p>
            </div>
          </div>
          <div class="field columns">
            <div class="column is-half">
              <label class="label">Email</label>
              <div class="control">
                <input class="input"
                  type="text"
                  v-model="user.email"
                  autocomplete="new-email">
                <span class="help is-danger"
                  v-if="!$v.user.email.required">Email is required
                </span>
              </div>
            </div>
            <div class="column is-half">
              <label class="label">Username</label>
              <div class="control">
                <input class="input"
                  type="text"
                  v-model="user.username"
                  autocomplete="new-username">
                <span class="help is-danger"
                  v-if="!$v.user.username.required">Username is required
                </span>
              </div>
            </div>
          </div>
          <div class="field columns">
            <div class="column is-half">
              <label class="label">Password</label>
              <div class="field has-addons">
                <div class="control is-expanded">
                  <input class="input"
                    :type="showPassword ? 'text' : 'password'"
                    v-model="user.password"
                    autocomplete="new-password">
                  <span class="help is-danger"
                    v-if="!$v.user.password.required">Password is required
                  </span>
                  <span class="help is-danger"
                    v-if="!$v.user.password.validatePassword">
                    {{ passwordMessage }}
                  </span>
                </div>
                <p class="control">
                  <button class="button"
                    @click="showPassword=!showPassword">
                    <span class="icon has-text-primary">
                      <span class="mso mso-24px">
                        {{ showPassword ? 'visibility' : 'visibility_off' }}
                      </span>
                    </span>
                  </button>
                </p>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
    <div v-else
      class="is-flex is-justify-content-center pt-5">
      <div class="notification is-danger is-flex is-justify-content-center is-size-4"
        style="width: 50%">
        You do not have permissions on this page
      </div>
    </div>
  </div>
</template>

<script>
import _cloneDeep from 'lodash/cloneDeep'
import _debounce from 'lodash/debounce'
import { AddressModel, EmailModel, PhoneModel } from '@/classes/viewmodels'
import { CountryCallingCodes, Locale, MalaysiaStates, StateAreaCodes } from '@/enums'
import CompanyAssetAddresses from './assets/CompanyAssetAddresses'
import CompanyAssetEmails from './assets/CompanyAssetEmails'
import CompanyAssetPhones from './assets/CompanyAssetPhones'
import ContactService from '../contact/ContactService'
import StoreMixin from './storeMixin'
import CompanyService from '../company/CompanyService'
// import Multiselect from 'vue-multiselect'
import { requiredIf } from 'vuelidate/lib/validators'
import CompanyContact from './assets/CompanyContact'
import SuperadminCompanyRoutes from '../superadmin/company/route-types'
import { BulmaAutocomplete } from '@/components/BulmaAutocomplete'
import { CompanySettingService, UserService } from '../../services'
import Guid from '../../components/Guid'
import CountrySettingService from '../superadmin/countrysetting/CountrySettingService'
import { TheMask } from 'vue-the-mask'
import { stripSpaces } from '@/utils/StringFunctions'

const companynameUnique = async function (value) {
  if (!value) {
    return true
  } else if (!this.$userInfo.isSystemAccount) {
    return true
  }
  try {
    const isUnique = await CompanySettingService.checkCompanyNameValidity(this.companyDetails.companyId, this.companyDetails.name.toString())
    return isUnique
  } catch (e) {
    console.error('Error checking company name validity:', e)
    return true
  }
}

const companyRegisteredIdUnique = async function (value) {
  if (!value) {
    return true
  }
  // Skip validation if input is disabled (readonly)
  if (!this.$userInfo.isSystemAccount) {
    return true
  }
  try {
    const isUnique = await CompanySettingService.validateCompanyRegisteredID(this.companyDetails.companyId, this.companyDetails.companyRegisteredId)
    return isUnique
  } catch (e) {
    console.error('Error checking company registered id validity:', e)
    return true
  }
}

const validatePassword = async function (password) {
  if (!password) {
    return false
  }
  try {
    var result = await UserService.getPasswordComplexity(password)
    this.passwordMessage = result.message
    return result.passwordMeetsCriteria
  } catch (e) {
    console.error('Error checking password validity:', e)
    return true
  }
}

export default {
  name: 'CompanyDetailSetting',
  components: {
    CompanyAssetAddresses,
    CompanyAssetEmails,
    CompanyAssetPhones,
    // Multiselect,
    CompanyContact,
    BulmaAutocomplete,
    TheMask
  },
  mixins: [StoreMixin],
  data() {
    return {
      attrs: [
        {
          key: 'today',
          highlight: {
            backgroundColor: '#ff8080'
          },
          popover: {
            label: 'Today'
          },
          dates: new Date()
        }
      ],
      companyDetails: {
        contacts: []
      },
      user: {},
      formats: {
        title: 'MMMM YYYY',
        weekdays: 'W',
        navMonths: 'MMM',
        input: ['DD/MM/YYYY'],
        data: ['DD/MM/YYYY'],
        dayPopover: null
      },
      // newContacts: [],
      hqCompanyNoOptions: [],
      hqCompanyNoLoading: false,
      selectedHqCompanyNo: null,
      showPassword: false,
      innerCustomerSince: null,
      passwordMessage: '',
      countries: [],
      timezones: []
    }
  },
  computed: {
    locales() {
      return this.companyDetails.countryCode ? Locale[this.companyDetails.countryCode] : []
    },
    mappingMalaysiaStates() {
      return MalaysiaStates
    },
    mappingStateAreaCodes() {
      return { ...StateAreaCodes.AUStateAreaCodes, ...StateAreaCodes.MYStateAreaCodes }
    },
    mappingCountryCallingCodes() {
      return CountryCallingCodes
    },
    sortedAddresses() {
      if (this.companyDetails && this.companyDetails.assetAddresses) {
        return [...this.companyDetails.assetAddresses].sort((a, b) => b.isDefault - a.isDefault)
      }
    },
    sortedEmails() {
      if (this.companyDetails && this.companyDetails.assetEmails) {
        return [...this.companyDetails.assetEmails].sort((a, b) => b.isDefault - a.isDefault)
      }
    },
    sortedPhones() {
      if (this.companyDetails && this.companyDetails.assetPhones) {
        return [...this.companyDetails.assetPhones].sort((a, b) => b.isDefault - a.isDefault)
      }
    },
    // timezones() {
    //   return this.companyDetails.countryCode ? Timezone[this.companyDetails.countryCode] : []
    // },
    validateError() {
      var isError = this.$v.$error
      if (
        this.$refs.assetPhones !== undefined &&
        this.$refs.assetEmails !== undefined &&
        this.$refs.assetAddresses !== undefined &&
        this.$refs.assetContacts !== undefined
      ) {
        isError =
          isError ||
          this.$refs.assetPhones.$v.$error ||
          !this.$refs.assetPhones.$v.uniquePhoneType ||
          this.$refs.assetEmails.$v.$error ||
          !this.$refs.assetEmails.$v.uniqueEmailType ||
          this.$refs.assetAddresses.$v.$error ||
          !this.$refs.assetAddresses.$v.uniqueAddressType ||
          this.$refs.assetContacts.$v.$error
      }
      return isError
    },
    isNew() {
      const { companyId } = this.$route.params
      return companyId === 'new'
    },
    isHQ() {
      const { companyType } = this.$route.params
      return companyType === 'hq'
    },
    today() {
      return new Date().toISOString().split('T')[0]
    }
  },
  watch: {
    companyDetails: {
      handler: function (val) {
        this.saveSnapshot({ entity: _cloneDeep(val) })
        if (typeof this.autoUpdate === 'function') this.autoUpdate(val)
      },
      deep: true
    },

    'companyDetails.countryCode': {
      handler: async function (newVal, oldVal) {
        if (oldVal && newVal && oldVal !== newVal) {
          this.companyDetails.assetAddresses = []
          this.companyDetails.timezones = []
          this.companyDetails.timeZoneId = null
          this.companyDetails.timezone = null
          this.companyDetails.locale = null
        }
        this.getTimezonesByCountryCode(newVal)
      }
    },

    'companyDetails.assetAddresses': {
      handler: async function (val) {
        if (!val.length > 0) return

        for (let address of val) {
          if (address.stateText in this.mappingMalaysiaStates) {
            address.stateCode = this.mappingMalaysiaStates[address.stateText]
          }

          if (address.stateCode in this.mappingStateAreaCodes) {
            address.stateAreaCode = this.mappingStateAreaCodes[address.stateCode]
          }

          if (address.countryCode in this.mappingCountryCallingCodes) {
            address.countryCallingCode = this.mappingCountryCallingCodes[address.countryCode]
          }
        }
      },
      deep: true
    },
    'companyDetails.assetEmails': {
      handler: async function (val) {
        if (!val.length > 0) return
        var defaultEmail = val.find((ae) => ae.isDefault)
        if (defaultEmail) {
          this.$set(this.user, 'email', defaultEmail.address)
        }
      },
      deep: true
    },
    innerCustomerSince: {
      handler: function (val) {
        if (val) {
          const dateTime = new Date(val)
          this.companyDetails.customerSince = dateTime.toISOString()
        } else {
          this.companyDetails.customerSince = null
        }
      }
    }
  },
  async created() {
    this.$showSpinner()
    if (this.isNew) {
      const newCompany = this.$store.getters['companysetting/newCompany']
      if (newCompany && newCompany.companyDetails) {
        this.companyDetails = _cloneDeep(newCompany.companyDetails)
      } else {
        this.companyDetails = _cloneDeep(await this.getStoreItem({ serviceFunction: 'getNewCompanyDetails' }))
      }
      this.user = {
        email: '',
        username: 'Administrator',
        password: '',
        isNew: true
      }
      this.companyDetails.isNew = true
    } else {
      this.companyDetails = _cloneDeep(await this.getStoreItem({ serviceFunction: 'getCompanyDetails', params: this.$route.params.companyId }))
    }
    if (this.companyDetails.customerSince) this.innerCustomerSince = new Date(this.companyDetails.customerSince).toISOString().split('T')[0]
    this.loadHQCompaniesData()
    this.getCountries()
    this.getTimezonesByCountryCode(this.companyDetails.countryCode)
    if (!this.isNew) {
      this.initializeAutoUpdate()
    }
    this.$hideSpinner()
  },
  methods: {
    async addContact(newContact) {
      // ContactService.getNewAssetContact('Company', Guid.newGuid()).then((response) => {
      //   const contact = response.data
      //   this.newContacts.push(contact)
      //   if (this.companyDetails.contacts == null) this.companyDetails.contacts = []
      //   this.companyDetails.contacts.push(contact)
      // })
      // const contact = new ContactModel(newContact.id, newContact.firstName, newContact.lastName)
      if (newContact == null) return
      if (this.companyDetails.contacts == null) this.companyDetails.contacts = []
      var contactMapId = Guid.newGuid()
      newContact.assets = [{ assetType: 0, assetId: this.companyDetails.companyId, id: contactMapId }]
      newContact.contactMapId = contactMapId
      this.companyDetails.contacts.push(newContact)
    },
    addTmpAddress() {
      let newAddress = new AddressModel(this.companyDetails.companyId)
      if (this.companyDetails.assetAddresses.length == 0) {
        newAddress.isDefault = true
      }
      this.companyDetails.assetAddresses.push(newAddress)
    },
    addTmpEmail() {
      let newEmail = new EmailModel()
      if (this.companyDetails.assetEmails.length == 0) {
        newEmail.isDefault = true
      }
      this.companyDetails.assetEmails.push(newEmail)
    },
    addTmpPhone() {
      let newPhone = new PhoneModel()
      if (this.companyDetails.assetPhones.length == 0) {
        newPhone.isDefault = true
      }
      this.companyDetails.assetPhones.push(newPhone)
    },
    customerSinceInputEvent(date) {
      const dateTime = new Date(date)
      //dateTime.setHours(0, 0, 0, 0)
      this.companyDetails.customerSince = dateTime.toISOString()
    },
    disableAutoUpdate() {
      if (typeof this.autoUpdate === 'function' && typeof this.autoUpdate.cancel === 'function') {
        this.autoUpdate.cancel()
      }
      this.autoUpdate = () => {}
    },
    initializeAutoUpdate() {
      this.autoUpdate = _debounce(async (value) => {
        this.snapshotDiff && (await this.updateCompanyDetails(value))
      }, this.debounceDelay)
    },
    removeAddress(address) {
      this.companyDetails.assetAddresses = this.companyDetails.assetAddresses.filter((a) => a !== address)
    },
    removeContact(contact) {
      this.companyDetails.contacts = this.companyDetails.contacts.filter((c) => c !== contact)
    },
    removeEmail(email) {
      this.companyDetails.assetEmails = this.companyDetails.assetEmails.filter((e) => e !== email)
    },
    removePhone(phone) {
      this.companyDetails.assetPhones = this.companyDetails.assetPhones.filter((p) => p !== phone)
    },
    setDefault(asset, assetType) {
      this.companyDetails[assetType].forEach((a) => (a.isDefault = a == asset))
    },
    async updateContacts(newContacts) {
      for (const newContact of newContacts) {
        // const contact = this.companyDetails.contacts.find((c) => c.contactId == newContact.id)
        // newContact.firstName = contact.firstName
        // newContact.lastName = contact.lastName
        if (newContact == null) continue
        newContact.id = newContact.contactId
        if (this.$userInfo.isSystemAccount) {
          await ContactService.postContact(newContact, this.companyDetails.companyId)
        } else await ContactService.postContact(newContact)
      }
    },
    async updateCompanyDetails(companyDetails) {
      if (companyDetails.abn) companyDetails.abn = stripSpaces(companyDetails.abn)
      if (this.isNew) {
        this.setStateProperty({ key: 'companyDetails', value: companyDetails })
        this.setStateProperty({ key: 'user', value: this.user })
        return
      }

      if (this.validateError) return this.$notification.error('Company Details', 'Please fix validation errors.')
      if (this.validateCountrySectionError()) return
      if (!this.snapshotDiff) return
      if (this.companyDetails.contacts.filter((c) => c.isNew).length > 0) {
        await this.updateContacts(this.companyDetails.contacts.filter((c) => c.isNew))
      }

      companyDetails.deepDiff = JSON.stringify(this.snapshotDiff)
      console.log('Updating company details with diff:', companyDetails.deepDiff)
      this.companyDetails = _cloneDeep(
        await this.editStoreItem({
          serviceFunction: 'updateCompanyDetails',
          entity: companyDetails,
          params: this.$route.params.companyId
        })
      )

      if (this.toastNotificationEnabled) {
        this.openToastNotification()
      }
    },
    validateCountrySectionError() {
      if (this.companyDetails.assetAddresses.length == 0 || this.companyDetails.timezone == null || this.companyDetails.locale == null) {
        this.$notification.error('Company Details', 'Require timezone, locale and address')
        return true
      }

      const [defaultAddress] = this.companyDetails.assetAddresses.filter((address) => address.isDefault)
      if (!defaultAddress || defaultAddress.countryCode !== this.companyDetails.countryCode) {
        this.$notification.error('Company Details', 'Default address country must match the selected country.')
        return true
      }
      return false
    },
    async loadHQCompaniesData() {
      this.hqCompanyNoLoading = true
      this.hqCompanyNoOptions = await CompanyService.getHQCompanies()
      this.hqCompanyNoLoading = false

      const selectedHqCompanyNo = this.companyDetails.hqCompanyNo
      if (selectedHqCompanyNo) {
        const selected = this.hqCompanyNoOptions.find((a) => a.companyLoginId === selectedHqCompanyNo)
        if (selected) {
          this.selectedHqCompanyNo = selected.companyLoginId
        }
      }
    },
    onHqSelected(hq) {
      if (hq) {
        this.selectedHqCompanyNo = hq.companyLoginId
        this.companyDetails.hqCompanyNo = hq.companyLoginId
        this.companyDetails.CompanyParentID = hq.companyLoginId
        this.companyDetails.parentCompanyName = hq.name
      } else {
        this.companyDetails.hqCompanyNo = null
        this.companyDetails.CompanyParentID = null
        this.companyDetails.parentCompanyName = null
      }
    },
    toggleActive(active) {
      this.companyDetails.isActive = active
    },
    preventNonDigit(event) {
      if (event.key === 'Backspace' || event.key === 'Tab' || event.key === 'ArrowLeft' || event.key === 'ArrowRight' || event.key === 'Delete') return
      if (!/[0-9]/.test(event.key)) {
        event.preventDefault()
      }
    },
    onRegisteredIdInput(event) {
      let val = event.target.value.replace(/\D/g, '').slice(0, 6)
      this.companyDetails.companyRegisteredId = val
      // this.checkCompanyRegisteredIdUnique()
    },
    resetPassword() {
      this.$router.push({
        name: SuperadminCompanyRoutes.CompanyUserListView.name,
        params: { companyId: this.$route.params.companyId }
      })
    },
    onHqCompanyNoTyping(text) {
      this.hqCompanyNoOptions = this.hqCompanyNoOptions.filter((item) => item.companyLoginId.toLowerCase().includes(text.toLowerCase()))
    },
    async getCountries() {
      var filter = {
        query: '',
        active: 1,
        sortColumn: 'countryCode',
        sortOrder: 'asc',
        pageIndex: 1,
        pageSize: 18
      }
      const result = await CountrySettingService.getEntitySummaries(filter)
      this.countries = result
    },
    async getTimezonesByCountryCode(countryCode) {
      if (!countryCode) return []
      const timezones = await CompanySettingService.getTimeZonesByCountryCode(countryCode)
      this.timezones = timezones
    }
  },
  validations: {
    companyDetails: {
      name: {
        required: requiredIf((model) => {
          return model.isNew == true
        }),
        companynameUnique
      },
      abn: {
        required: requiredIf((model) => {
          return model.isNew == true
        })
      },
      companyRegisteredId: {
        required: requiredIf((model) => {
          return model.isNew == true
        }),
        companyRegisteredIdUnique
      }
    },
    user: {
      email: {
        required: requiredIf((model) => {
          return model.isNew == true
        })
      },
      username: {
        required: requiredIf((model) => {
          return model.isNew == true
        })
      },
      password: {
        required: requiredIf((model) => {
          return model.isNew == true
        }),
        validatePassword
      }
    }
  },
  async beforeRouteLeave(to, from, next) {
    this.disableAutoUpdate()
    this.toastNotificationEnabled = true
    await this.updateCompanyDetails(this.companyDetails)
    this.clearSnapshots()
    this.toastNotificationEnabled = false
    next()
  }
}
</script>

<style>
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>